/**
 * The app-wide banner for the sandbox admin's maintenance windows and incidents (and any message sent "as a banner"):
 * slim, above the page, coloured by kind, times in the viewer's own time zone ("Maintenance today 18:00–19:00").
 * Each person can close it; when the admin changes the message (an incident's status, a new window) it shows again.
 * Mounted once at the app layout level (App.jsx), not inside the top bar.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { XIcon } from '@phosphor-icons/react';
import useSandboxMessages from '../../hooks/useSandboxMessages';
import { bannerMessages, isMultiline, linkSegments, messageHeadline, plainLine } from '../../utils/sandboxMessages';
import MessageIcon from './MessageIcon';
import MessageText from './MessageText';
import './SandboxMessages.css';

const MAX_SHOWN = 2;

/** Whether the text is cut (one line on a computer, two on a phone): measured, and again when the window resizes. */
function useIsClamped(ref, deps) {
  const [clamped, setClamped] = useState(false);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) { return undefined; }
    const measure = () => setClamped(node.scrollWidth > node.clientWidth + 1 || node.scrollHeight > node.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') { return undefined; }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return clamped;
}

/**
 * One banner: the headline and the text as one plain line, with "Read more" when it does not fit or the text has more
 * lines (paragraphs, a list); open, it shows the text's markdown.
 */
function Banner({ m, dismiss, read }) {
  const [open, setOpen] = useState(false);
  const textRef = useRef(null);
  const clamped = useIsClamped(textRef, [m.id, m.version, open]);
  useEffect(() => { setOpen(false); }, [m.id, m.version]);
  const tone = m.kind === 'incident' && m.status === 'resolved' ? 'resolved' : m.kind;
  const markRead = () => { if (!m.read) { read([m.id]); } };
  const textId = `sandbox-msg-${m.id}-text`;
  return (
    <div className={`sandbox-msg-banner is-${tone}${open ? ' is-open' : ''}`} data-message-id={m.id}>
      <span className="sandbox-msg-banner-icon"><MessageIcon kind={m.kind} size={16} status={m.status} /></span>
      <span className="sandbox-msg-banner-text" id={textId} ref={textRef}>
        <strong>{messageHeadline(m)}</strong>
        {m.body && (open
          ? <MessageText className="sandbox-msg-banner-body" text={m.body} />
          : (
            <span className="sandbox-msg-banner-body">
              {linkSegments(plainLine(m.body)).map((part, i) => (part.href
                ? <a href={part.href} key={i} rel="noopener noreferrer" target="_blank">{part.text}</a>
                : <React.Fragment key={i}>{part.text}</React.Fragment>))}
            </span>
          ))}
      </span>
      {(clamped || open || isMultiline(m.body)) && (
        <button
          aria-controls={textId}
          aria-expanded={open}
          className="sandbox-msg-banner-more"
          onClick={() => { setOpen(!open); markRead(); }}
          type="button"
        >
          {open ? 'Show less' : 'Read more'}
        </button>
      )}
      {m.link && (
        <a className="sandbox-msg-banner-link" href={m.link} onClick={markRead} rel="noopener noreferrer" target="_blank">Details</a>
      )}
      <button
        aria-label={`Dismiss: ${m.title}`}
        className="sandbox-msg-banner-close"
        onClick={() => { dismiss(m.id); markRead(); }}
        title="Dismiss"
        type="button"
      >
        <XIcon aria-hidden="true" size={14} />
      </button>
    </div>
  );
}

Banner.propTypes = {
  dismiss: PropTypes.func.isRequired,
  m: PropTypes.object.isRequired,
  read: PropTypes.func.isRequired,
};

export default function SandboxMessagesBanner() {
  const { messages, dismiss, read } = useSandboxMessages();
  const shown = bannerMessages(messages?.items).slice(0, MAX_SHOWN);
  // The live region stays mounted so screen readers announce a banner that appears later.
  return (
    <div aria-live="polite" className="sandbox-msg-banners" data-testid="sandbox-messages-banner" role="status">
      {shown.map((m) => <Banner dismiss={dismiss} key={`${m.id}:${m.version}`} m={m} read={read} />)}
    </div>
  );
}
